/** Cliente da API. Tudo passa pelo mesmo site (/api), com o cookie de sessão. */
import { FOCUS_ENTITY_HEADER } from '@loot/shared';
import type {
  AcceptanceMode,
  CancellationStatus,
  PeriodFrequency,
  PurchaseIndicators,
  ReceivingCriteriaItem,
  ReversalTarget,
} from '@loot/shared';

import type {
  ContractForPurchase,
  ForecastFrequency,
  PurchaseContractCheck,
  PurchaseContractingMode,
  RegistryStatus,
  SingleHolderReason,
} from '@loot/shared';
import { focusEntity, setFocusEntity } from './focus.ts';

export interface ApiIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly issues: ApiIssue[] = [],
    readonly requestId?: string,
    /** O restante da resposta de erro (ex.: o que impede a escolha de uma proposta). */
    readonly details: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

/** Idioma da tela (o i18n grava em <html lang>): o servidor responde a validação nele. */
function uiLanguage(): string {
  return document.documentElement.lang || 'pt-BR';
}

/** Empresa em foco (ADR 0100): só nas consultas e nas exportações, que são a lista da tela. */
function focusHeader(): Record<string, string> {
  const focus = focusEntity();
  return focus ? { [FOCUS_ENTITY_HEADER]: focus.id } : {};
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      'accept-language': uiLanguage(),
      ...(method === 'GET' ? focusHeader() : {}),
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    // A pessoa perdeu o acesso à empresa em foco: volta para todas, em vez de ficar sem dados.
    if (data.code === 'focus_not_allowed') setFocusEntity(null);
    throw new ApiError(
      res.status,
      typeof data.code === 'string' ? data.code : 'internal_error',
      Array.isArray(data.issues) ? (data.issues as ApiIssue[]) : [],
      typeof data.requestId === 'string' ? data.requestId : undefined,
      data,
    );
  }
  return data as T;
}

/** Envia um formulário com arquivo (multipart). O navegador monta o cabeçalho sozinho. */
async function upload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'accept-language': uiLanguage() },
    body: form,
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiError(
      res.status,
      typeof data.code === 'string' ? data.code : 'internal_error',
      Array.isArray(data.issues) ? (data.issues as ApiIssue[]) : [],
      typeof data.requestId === 'string' ? data.requestId : undefined,
      data,
    );
  }
  return data as T;
}

/** Baixa um arquivo gerado pela API (ex.: exportação em Excel), enviando os filtros no corpo. */
async function download(path: string, body: unknown): Promise<void> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'accept-language': uiLanguage(),
      'content-type': 'application/json',
      ...focusHeader(),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    throw new ApiError(
      res.status,
      typeof data.code === 'string' ? data.code : 'internal_error',
      Array.isArray(data.issues) ? (data.issues as ApiIssue[]) : [],
      typeof data.requestId === 'string' ? data.requestId : undefined,
      data,
    );
  }
  const name =
    /filename*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(
      res.headers.get('content-disposition') ?? '',
    )?.[1] ?? 'export.xlsx';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = decodeURIComponent(name);
  a.click();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  delete: (path: string) => request<void>('DELETE', path),
  upload,
  download,
};

// ----- Tipos devolvidos pela API -----

export type LocalizedText = { 'pt-BR': string; en: string };
export type ScopeType = 'group' | 'legal_entity' | 'branch';

export interface Me {
  user: { id: string; email: string; displayName: string; locale: string; timezone: string };
  /** Segunda etapa (S2): permissões sensíveis retidas nesta sessão e onde ativar o segundo fator. */
  mfa?: { verified: boolean; withheld: string[]; setupUrl: string };
  /**
   * Administrador master (ADR 0051): o botão Excluir aparece só com a exclusão ligada.
   * `exceptionUntil`: fim da fase de desenvolvimento (ADR 0053), ou null fora dela.
   */
  master?: { active: boolean; purgeEnabled: boolean; exceptionUntil?: string | null };
  /** Nomeado aprovador (ADR 0080): vê Aprovações e Ausências mesmo sem perfil. */
  approver?: boolean;
  permissions: {
    permission: string;
    scopes: { scopeType: ScopeType; legalEntityId: string | null; branchId: string | null }[];
  }[];
}

export interface LegalEntity {
  id: string;
  code: string;
  legalName: string;
  tradeName: string | null;
  kind: string;
  country: string;
  taxId: string | null;
  functionalCurrency: string;
  taxRegime: string | null;
  timezone: string;
  defaultLocale: 'pt-BR' | 'en';
  parentLegalEntityId: string | null;
  ownershipPct: string | null;
  isActive: boolean;
}

export interface Branch {
  id: string;
  legalEntityId: string;
  code: string;
  name: string;
  kind: string;
  country: string;
  timezone: string;
  taxId: string | null;
  stateRegistration: string | null;
  municipalRegistration: string | null;
  address: Record<string, string | null>;
  isActive: boolean;
}

export interface Currency {
  code: string;
  name: LocalizedText;
  minorUnits: number;
  isActive: boolean;
}

export interface ExchangeRate {
  id: string;
  rateDate: string;
  baseCurrency: string;
  quoteCurrency: string;
  rate: string;
  rateType: string;
  source: string;
  /** Taxa de correção: a taxa que esta substitui e o motivo (ADR 0032). */
  replacesRateId: string | null;
  correctionReason: string | null;
  /** A correção que substituiu esta taxa (null = vigente). */
  replacedById: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface Role {
  id: string;
  code: string;
  name: LocalizedText;
  description: Partial<LocalizedText>;
  isSystem: boolean;
  isActive: boolean;
  permissions: string[];
  /** Classificação na matriz de perfis delegáveis (ADR 0042). */
  tier: AccessTier;
}

export type AccessTier = 'normal' | 'sensitive' | 'restricted' | 'critical';

/** Pedido de acesso com aprovação por outra pessoa (ADR 0042). */
export interface AccessRequest {
  id: string;
  kind: 'grant' | 'revoke' | 'unblock' | 'reclassify';
  userId: string;
  userName: string | null;
  roleId: string | null;
  roleCode: string | null;
  roleName: LocalizedText | null;
  scopeType: ScopeType;
  legalEntityId: string | null;
  branchId: string | null;
  tier: Exclude<AccessTier, 'normal'>;
  justification: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requestedBy: string;
  requestedByName: string | null;
  requestedAt: string;
  expiresAt: string;
  decidedByName: string | null;
  decisionNote: string | null;
  expired: boolean;
  canDecide: boolean;
  canCancel: boolean;
}

export interface Assignment {
  id: string;
  roleId: string;
  roleCode: string;
  roleName: LocalizedText;
  scopeType: ScopeType;
  legalEntityId: string | null;
  branchId: string | null;
  tier: AccessTier;
  /** Liberação de responsável único (ADR 0043): fim da validade e se ainda vale. */
  expiresAt: string | null;
  singleHolderReleaseId: string | null;
  inForce: boolean;
}

export type SingleHolderReleaseStatus =
  | 'pending'
  | 'cancelled'
  | 'lapsed'
  | 'active'
  | 'withdrawn'
  | 'expired'
  | 'superseded'
  | 'revoked';

export interface SingleHolderRelease {
  id: string;
  status: SingleHolderReleaseStatus;
  justification: string;
  requestedAt: string;
  effectiveFrom: string;
  activateUntil: string;
  activatedAt: string | null;
  expiresAt: string | null;
  endedAt: string | null;
  endReason: string | null;
  canCancel: boolean;
  canActivate: boolean;
  canWithdraw: boolean;
}

/** Situação da liberação de responsável único da pessoa logada (ADR 0043). */
export interface SingleHolderStatus {
  policy: {
    roleCode: string;
    roleName: LocalizedText;
    scopeType: 'group';
    graceHours: number;
    validityDays: number;
    activationDays: number;
    reauthMinutes: number;
  } | null;
  eligible: boolean;
  reason: SingleHolderReason | null;
  strongAuth: boolean;
  releases: SingleHolderRelease[];
}

export interface User {
  id: string;
  email: string;
  displayName: string;
  locale: 'pt-BR' | 'en';
  timezone: string;
  isActive: boolean;
  linked: boolean;
  lastLoginAt: string | null;
  assignments: Assignment[];
  /** Só a conta de pessoa aprova pedidos de acesso (ADR 0042). */
  accountKind: AccountKind;
  blockedAt: string | null;
  blockReason: string | null;
  accountKindSetBy: string | null;
  accountKindSetAt: string | null;
  /** Administrador master (ADR 0051): só leitura, a marca muda só pela instalação. */
  isMaster: boolean;
}

export type AccountKind = 'unclassified' | 'person' | 'generic' | 'technical';

export interface PermissionCatalog {
  modules: Record<string, LocalizedText>;
  permissions: { code: string; module: string; label: LocalizedText; groupOnly?: boolean }[];
}

export interface AuditEntry {
  id: number;
  occurredAt: string;
  actorUserId: string | null;
  tableSchema: string;
  tableName: string;
  recordId: string | null;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  changedFields: string[] | null;
  oldData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
}

export interface SecurityEvent {
  id: number;
  occurredAt: string;
  kind: string;
  userId: string | null;
  email: string | null;
  clientIp: string | null;
  details: Record<string, unknown>;
}

export interface HelpArticleSummary {
  id: string;
  slug: string;
  title: LocalizedText;
  summary: LocalizedText;
  isPublished: boolean;
  sortOrder: number;
  updatedAt: string;
}

export interface HelpArticle extends HelpArticleSummary {
  body: LocalizedText;
}

export interface Department {
  id: string;
  /** Vazio = departamento corporativo (todas as empresas). */
  legalEntityId: string | null;
  legalEntityCode: string | null;
  code: string;
  name: string;
  /** Centro de custo oficial (migração 0244). */
  costCenterId: string | null;
  costCenterCode: string | null;
  costCenterName?: string | null;
  /** Só o código antigo, sem centro ligado: a tela pede para escolher o centro. */
  costCenterUnlinked?: boolean;
  managerUserId: string | null;
  substituteUserId: string | null;
  managerName: string | null;
  substituteName: string | null;
  isActive: boolean;
  netsuiteCode: string | null;
  canEdit: boolean;
}

/** Área ativa, para escolher de onde sai o dinheiro (sem precisar de acesso à empresa). */
export interface DepartmentChoice {
  id: string;
  code: string;
  name: string;
  legalEntityId: string;
  legalEntityCode: string | null;
  legalEntityName: string;
}

export interface Person {
  id: string;
  displayName: string;
  email: string;
}

// ----- Compras -----

export type PurchaseStatus =
  | 'awaiting_need_approval'
  | 'new'
  | 'in_review'
  | 'awaiting_info'
  | 'quoting'
  | 'awaiting_approval'
  | 'approved'
  | 'rejected'
  | 'ordered'
  | 'partially_delivered'
  | 'completed'
  | 'cancelled';

export interface PurchaseOptions {
  departments: Omit<DepartmentChoice, 'legalEntityId'>[];
  currencies: { code: string; name: LocalizedText }[];
}

export interface PurchaseSummary {
  id: string;
  number: number;
  title: string;
  status: PurchaseStatus;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  neededBy: string | null;
  legalEntityId: string;
  costCenterId: string | null;
  department: { id: string; code: string; name: string; legalEntityCode: string | null } | null;
  requester: { id: string; name: string | null };
  buyer: { id: string; name: string | null } | null;
  estimatedAmount: string | null;
  estimatedCurrency: string | null;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Pendências de Compras da pessoa (acompanhamento). */
export interface PurchaseOverview {
  today: string;
  requester: { open: number; awaitingAnswer: number; awaitingNeed: number; overdue: number };
  /** Compras em que a pessoa é o responsável técnico indicado (ADR 0046). */
  technical: {
    id: string;
    number: number;
    title: string;
    status: PurchaseStatus;
    legalEntityCode: string;
  }[];
  buyer: {
    open: number;
    unassigned: number;
    mine: number;
    overdue: number;
    urgent: number;
    byStatus: Partial<Record<PurchaseStatus, number>>;
  } | null;
  /** Entregas e recebimento, para quem compra ou recebe (sem valores). */
  receiving: {
    openOrders: number;
    expected: number;
    late: number;
    partial: number;
    withBalance: number;
    productsAwaiting: number;
    servicesAwaiting: number;
    openDivergences: number;
    cancellationsInProgress: number;
    reversals30d: number;
    completed30d: number;
  } | null;
}

/** Indicadores operacionais de Compras num período (só leitura). */
export interface PurchaseIndicatorsView extends PurchaseIndicators {
  from: string;
  to: string;
  departments: Record<string, { code: string; name: string; legalEntityCode: string | null }>;
  buyers: Record<string, string>;
}

export interface PurchaseItem {
  id: string;
  lineNo: number;
  kind: 'product' | 'service';
  description: string;
  quantity: string;
  unit: string;
  specification: string | null;
  referenceUrl: string | null;
  acceptanceMode: AcceptanceMode | null;
  acceptanceMilestones: { description: string; quantity: string }[] | null;
  periodFrequency: PeriodFrequency | null;
  periodStart: string | null;
  periodEnd: string | null;
  periodQuantityRule: 'fixed' | 'variable' | null;
  quantityPerPeriod: string | null;
  requiresAssetRegistration: boolean;
  /** Item rateado com centro de marketing: afeta o CAC? */
  affectsCac: boolean | null;
  /** Software do catálogo da TI que o item compra (ADR 0094). */
  softwareId: string | null;
  softwareName: string | null;
  /** Serviço recorrente: periodicidade e duração em meses (valor de 12 meses na alçada). */
  recurrenceFrequency: PeriodFrequency | null;
  recurrenceMonths: number | null;
  /** Classificação financeira oficial e origem no orçamento (ADR 0055). */
  expenseTypeId: string | null;
  classificationId: string | null;
  budgetVersionId: string | null;
  budgetExpenseId: string | null;
  budgetReferenceLabel: string | null;
  noBudgetJustification: string | null;
}

export type CriteriaStatus = 'missing' | 'stale' | 'valid';

/** Responsável técnico atual: o indicado por Compras ou, sem indicação, o solicitante. */
export interface TechnicalOwnerView {
  userId: string;
  name: string;
  designated: boolean;
  reason: string | null;
  assignedAt: string | null;
  impeded: boolean;
}

/** Critérios de recebimento, para quem compra (ADR 0046). */
export interface ReceivingView {
  status: CriteriaStatus;
  items: (ReceivingCriteriaItem & {
    lineNo: number;
    description: string;
    /** Quantidade pedida; `quantity` é a de referência (contratada, quando houver). */
    requestedQuantity: string;
    /** Periodicidade do serviço recorrente informada antes da aprovação (sugestão). */
    suggestedFrequency: PeriodFrequency | null;
    contracted: boolean;
    issues: string[];
    warnings: string[];
  })[];
  validations: {
    id: string;
    kind: 'initial' | 'change' | 'migration';
    justification: string | null;
    validatedAt: string;
    validatedBy: { id: string; name: string | null } | null;
    current: boolean;
  }[];
  technicalOwner: TechnicalOwnerView | null;
  designations: {
    id: string;
    user: { id: string; name: string | null } | null;
    reason: string;
    assignedAt: string;
    assignedBy: { id: string; name: string | null } | null;
    endedAt: string | null;
  }[];
  actions: {
    validate: boolean;
    justificationRequired: boolean;
    serviceCriteriaLocked: boolean;
    designate: boolean;
  };
}

/** Tela restrita do responsável técnico: só o necessário ao aceite, sem valores (ADR 0046, P1). */
/** Entregas a receber: pedidos ativos, sem valores (ADR 0046, Entrega 2). */
export interface DeliveryToReceive {
  requestId: string;
  requestNumber: number;
  title: string;
  requestStatus: PurchaseStatus;
  legalEntityCode: string;
  orderNumber: number;
  supplierName: string;
  orderStatus: 'issued' | 'sent';
  expectedDelivery: string | null;
  pendingItems: number;
}

export interface DeliveryAttachment {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  documentKind: string;
  documentRef: string | null;
  gateProtocolId: string | null;
  receiptId: string | null;
  acceptanceId: string | null;
  divergenceId: string | null;
  createdAt: string;
  createdBy: string | null;
}

/** Registro de estorno, mostrado junto do recebimento, conferência ou cancelamento estornado. */
export interface DeliveryReversalRef {
  id: string;
  reason: string;
  reversedAt: string;
  reversedBy: string | null;
}

/** Cancelamento de saldo a confirmar pelo gestor do centro de custo (ou substituto). */
export interface CancellationToConfirm {
  cancellationId: string;
  requestId: string;
  requestNumber: number;
  title: string;
  legalEntityCode: string;
  orderNumber: number;
  supplierName: string;
  requestedAt: string;
  requestedBy: string | null;
}

/** Quadro do recebimento de uma compra, sem preços (ADR 0046, Entregas 2 e 4). */
export interface DeliveryBoard {
  request: {
    id: string;
    number: number;
    title: string;
    status: PurchaseStatus;
    legalEntityId: string;
    legalEntity: { code: string; name: string };
    requester: string;
    requesterUserId: string;
    buyerUserId: string | null;
    buyer: string | null;
    costCenterId: string | null;
  };
  technicalOwner: { userId: string; name: string; impeded: boolean } | null;
  order: {
    id: string;
    number: number;
    status: 'issued' | 'sent';
    supplierName: string;
    issuedAt: string;
    sentOn: string | null;
    expectedDelivery: string | null;
    deliveryInstructions: string | null;
  } | null;
  items: {
    orderItemId: string;
    requestItemId: string;
    lineNo: number;
    kind: 'product' | 'service';
    description: string;
    unit: string;
    contracted: string;
    cancelled: string;
    cancelable: string;
    received: string;
    accepted: string;
    rejected: string;
    good: string;
    receivable: string;
    awaiting: string;
    pending: string;
    openDivergence: string;
    requiresAssetRegistration: boolean;
    acceptanceMode: AcceptanceMode | null;
    /** Etapas validadas, com o que já está em boas condições em cada uma. */
    milestones: { no: number; description: string; quantity: string; good: string }[] | null;
    periodFrequency: PeriodFrequency | null;
    periodStart: string | null;
    periodEnd: string | null;
    periodQuantityRule: 'fixed' | 'variable' | null;
    quantityPerPeriod: string | null;
  }[];
  gateProtocols: {
    id: string;
    arrivedAt: string;
    reference: string | null;
    notes: string | null;
    registeredAt: string;
    registeredBy: string | null;
  }[];
  receipts: {
    id: string;
    seq: number;
    /** product: recebimento físico; service: execução de serviço informada. */
    kind: 'product' | 'service';
    gateProtocolId: string | null;
    arrivedOn: string;
    notes: string | null;
    registeredAt: string;
    registeredBy: string | null;
    registeredByUserId: string;
    technicalOwner: string | null;
    notifiedAt: string | null;
    reversal: DeliveryReversalRef | null;
    lines: {
      id: string;
      orderItemId: string;
      quantity: string;
      notes: string | null;
      milestoneNo: number | null;
      periodStart: string | null;
      workOrderCode: string | null;
      awaiting: string;
    }[];
  }[];
  acceptances: {
    id: string;
    receiptId: string;
    notes: string | null;
    decidedAt: string;
    decidedBy: string | null;
    decidedByUserId: string;
    reversal: DeliveryReversalRef | null;
    lines: { receiptLineId: string; orderItemId: string; quantity: string }[];
  }[];
  divergences: {
    id: string;
    origin: 'receipt' | 'acceptance';
    receiptId: string;
    acceptanceId: string | null;
    orderItemId: string;
    kind: 'excess' | 'damaged' | 'wrong_item' | 'nonconforming';
    quantity: string;
    description: string;
    status: 'open' | 'returned' | 'new_purchase' | 'reversed';
    resolutionNote: string | null;
    newRequestId: string | null;
    newRequestNumber: number | null;
    createdAt: string;
    createdBy: string | null;
    resolvedAt: string | null;
    resolvedBy: string | null;
  }[];
  cancellations: {
    id: string;
    status: CancellationStatus;
    reason: string;
    impactDeclared: boolean;
    impactDescription: string | null;
    requestedAt: string;
    requestedBy: string | null;
    requestedByUserId: string;
    decidedAt: string | null;
    decidedBy: string | null;
    decidedByUserId: string | null;
    decisionNote: string | null;
    executedAt: string | null;
    executedBy: string | null;
    withdrawnAt: string | null;
    withdrawnBy: string | null;
    withdrawalNote: string | null;
    /** Quem pode confirmar agora (vazio: espera, N1). Só no cancelamento aguardando confirmação. */
    confirmers: string[] | null;
    reversal: DeliveryReversalRef | null;
    lines: { orderItemId: string; quantity: string }[];
  }[];
  reversals: {
    id: string;
    target: ReversalTarget;
    receiptId: string | null;
    acceptanceId: string | null;
    cancellationId: string | null;
    reason: string;
    afterCompletion: boolean;
    assetWarning: boolean;
    reversedAt: string;
    reversedBy: string | null;
  }[];
  attachments: DeliveryAttachment[];
  roles: {
    buyer: boolean;
    canReceive: boolean;
    canAccept: boolean;
    canConfirm: boolean;
    canInformExecution: boolean;
  };
  actions: {
    recordGateProtocol: boolean;
    recordReceipt: boolean;
    informExecution: boolean;
    accept: boolean;
    resolveDivergence: boolean;
    requestCancellation: boolean;
    confirmCancellation: string | null;
    executeCancellation: string | null;
    withdrawCancellation: string | null;
    reverse: { receipts: string[]; acceptances: string[]; cancellations: string[] };
  };
}

export interface TechnicalView {
  id: string;
  number: number;
  title: string;
  status: PurchaseStatus;
  neededBy: string | null;
  legalEntity: { code: string; name: string };
  department: string;
  requester: string;
  buyer: string | null;
  designation: { reason: string; assignedAt: string } | null;
  items: {
    id: string;
    lineNo: number;
    kind: 'product' | 'service';
    description: string;
    quantity: string;
    unit: string;
    specification: string | null;
    referenceUrl: string | null;
    contractedQuantity: string | null;
    acceptanceMode: AcceptanceMode | null;
    milestones: { description: string; quantity: string }[];
    periodFrequency: PeriodFrequency | null;
    periodStart: string | null;
    periodEnd: string | null;
    periodQuantityRule: 'fixed' | 'variable' | null;
    quantityPerPeriod: string | null;
    requiresAssetRegistration: boolean;
  }[];
  order: {
    number: number;
    status: PurchaseOrderStatus;
    supplierName: string;
    issuedAt: string;
    sentOn: string | null;
    expectedDelivery: string | null;
    deliveryInstructions: string | null;
  } | null;
  attachments: {
    id: string;
    itemId: string;
    fileName: string;
    contentType: string;
    sizeBytes: number;
  }[];
}

export interface PurchaseEvent {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  actorName: string | null;
  kind:
    | 'created'
    | 'edited'
    | 'status_changed'
    | 'assigned'
    | 'comment'
    | 'attachment_added'
    | 'attachment_removed'
    | 'quotation_opened'
    | 'quotation_cancelled'
    | 'quotation_reopened'
    | 'supplier_invited'
    | 'supplier_declined'
    | 'supplier_removed'
    | 'proposal_recorded'
    | 'proposal_withdrawn'
    | 'proposal_selected'
    | 'approval_requested'
    | 'approval_decided'
    | 'order_issued'
    | 'order_sent'
    | 'order_updated'
    | 'order_cancelled'
    | 'receiving_criteria_validated'
    | 'technical_owner_assigned'
    | 'gate_protocol_recorded'
    | 'receipt_recorded'
    | 'acceptance_recorded'
    | 'divergence_recorded'
    | 'divergence_resolved'
    | 'contracting_mode_set';
  fromStatus: PurchaseStatus | null;
  toStatus: PurchaseStatus | null;
  comment: string | null;
  data: {
    buyerUserId?: string | null;
    buyerName?: string | null;
    answer?: boolean;
    /** Troca da resposta do CAC (evento "alterou o pedido"). */
    affectsCacFrom?: boolean;
    affectsCacTo?: boolean;
    fileName?: string;
    number?: number;
    supplierName?: string | null;
    version?: number;
    exception?: boolean;
    approvalType?: string;
    outcome?: 'approved' | 'rejected' | 'returned';
    sentOn?: string;
    expectedDelivery?: string | null;
    revertOutcome?: 'reverted' | 'blocked';
    /** Modalidade de contratação definida pelo comprador (ADR 0102). */
    mode?: 'new_purchase' | 'within_contract';
    contractCode?: string | null;
    /** Solicitação encerrada como cobrança de contrato (vai pelo Contas a Pagar). */
    contractBilling?: boolean;
    /** Pedido conferido com o contrato ativo (fluxo C), sem valores. */
    contractCheck?: {
      status: 'ok' | 'no_amount' | 'currency_mismatch' | 'above';
      contractCode: string;
      justification: string | null;
    } | null;
    blockers?: string[];
    name?: string | null;
    /** Mudança de situação feita pelo recebimento (ADR 0046, Entrega 2). */
    automatic?: boolean;
    seq?: number;
    arrivedOn?: string;
    reference?: string | null;
    kind?: 'excess' | 'damaged' | 'wrong_item';
    quantity?: string;
    status?: 'open' | 'returned' | 'new_purchase';
  };
}

export interface PurchaseDetail extends PurchaseSummary {
  justification: string;
  departmentId: string;
  /** "Essa compra afetará o CAC?" (centros de marketing); null = não se aplica. */
  affectsCac: boolean | null;
  /** Compra em marketplace ou loja física (R1): vendedor e link do anúncio. */
  marketplace: { sellerName: string | null; link: string | null } | null;
  /**
   * Tipo da compra (ADR 0102): o escolhido por quem pede ou, nas solicitações antigas, o dos itens
   * (materiais, bens e equipamentos, serviço simples ou recorrente).
   */
  requestType: 'MATERIAIS' | 'BENS' | 'SERVICO_SIMPLES' | 'SERVICO_RECORRENTE';
  /** O tipo foi escolhido por quem pede (falso nas antigas, em que vem dos itens). */
  requestTypeInformed: boolean;
  /** Modalidade de contratação, do comprador na triagem (ADR 0102); nula = não definida. */
  contractingMode: PurchaseContractingMode | null;
  /** Contrato que quem pede indicou em texto (só orienta o comprador). */
  contractReference: string | null;
  /** Contrato da compra dentro de contrato: dados mínimos para quem compra; só o código aos demais. */
  contract: (ContractForPurchase | { id: string; code: string | null }) | null;
  costCenterId: string | null;
  /** Centro de Custo Master: o gestor dele aprova a necessidade (ADR 0094). */
  masterCostCenter: { id: string; code: string; name: string; approverName: string | null } | null;
  /** Pedidos de aprovação da compra, do mais novo ao mais antigo. */
  approvals: {
    id: string;
    approvalType: 'purchasing.need' | 'purchasing.contracting';
    status: string;
    blockedReason: string | null;
    createdAt: string;
  }[];
  /** Pedidos ao fornecedor, sem valores (ADR 0045): o ativo primeiro. */
  orders: PurchaseOrderOverview[];
  /** Critérios de recebimento e responsável técnico (ADR 0046), sem preços. */
  receiving: { criteriaStatus: CriteriaStatus; technicalOwner: TechnicalOwnerView | null };
  /** A escolha da proposta, para quem compra e para quem aprova a contratação (sem preços). */
  contracting: {
    supplierName: string | null;
    selectionJustification: string | null;
    minProposals: number | null;
    validProposals: number | null;
    exception: boolean;
    exceptionJustification: string | null;
    decidedAt: string | null;
    /** A conta do valor enviado à aprovação, quando há serviços recorrentes (sem preços por item). */
    approvalBasis: {
      contractTotal: string;
      approvalAmount: string;
      currency: string;
      recurring: { description: string; frequency: PeriodFrequency; months: number }[];
    } | null;
  } | null;
  resumeStatus: PurchaseStatus | null;
  items: PurchaseItem[];
  /** Nomes oficiais do Tipo de Gasto e da Classificação dos itens. */
  classifications: { id: string; code: string | null; name: string }[];
  attachments: PurchaseAttachment[];
  events: PurchaseEvent[];
  actions: {
    edit: boolean;
    assign: boolean;
    answer: boolean;
    statusTargets: PurchaseStatus[];
    comment: boolean;
    attach: boolean;
    quote: boolean;
  };
}

export type PurchaseOrderStatus = 'issued' | 'sent' | 'cancelled';

/** Resumo do pedido ao fornecedor que quem pediu e quem aprova veem (sem valores). */
export interface PurchaseOrderOverview {
  id: string;
  number: number;
  status: PurchaseOrderStatus;
  supplierName: string;
  issuedAt: string;
  sentOn: string | null;
  expectedDelivery: string | null;
  cancelledAt: string | null;
  revertOutcome: 'reverted' | 'blocked' | null;
}

type PersonRef = { id: string; name: string | null } | null;

/** Pedido ao fornecedor completo, para quem compra. */
export interface PurchaseOrderDetail {
  id: string;
  number: number;
  displayNumber: string;
  status: PurchaseOrderStatus;
  partnerId: string;
  supplierName: string;
  currency: string;
  freightAmount: string;
  totalAmount: string;
  paymentTerms: string | null;
  deliveryDays: number | null;
  proposalValidUntil: string | null;
  expectedDelivery: string | null;
  deliveryInstructions: string | null;
  notes: string | null;
  issuedAt: string;
  issuedBy: PersonRef;
  validity: {
    confirmedOn: string;
    confirmedBy: PersonRef;
    evidence: string | null;
    attachmentId: string | null;
  } | null;
  sentOn: string | null;
  sentNote: string | null;
  sentAt: string | null;
  sentBy: PersonRef;
  cancelledAt: string | null;
  cancelledBy: PersonRef;
  cancelReason: string | null;
  revertOutcome: 'reverted' | 'blocked' | null;
  revertBlockers: string[] | null;
  items: {
    id: string;
    lineNo: number;
    kind: 'product' | 'service';
    description: string;
    quantity: string;
    unit: string;
    unitPrice: string;
    lineTotal: string;
    notes: string | null;
  }[];
}

export interface PurchaseOrderView {
  today: string;
  request: { id: string; number: number; title: string; status: PurchaseStatus };
  legalEntity: { code: string; legalName: string } | null;
  orders: PurchaseOrderDetail[];
  actions: { issue: boolean; send: boolean; updateDelivery: boolean; cancel: boolean };
  /** Critérios de recebimento validados por Compras (sem isso o pedido não sai). */
  receivingCriteria: CriteriaStatus;
  /** Contrato antes do pedido (D-02; regra provisória da D-01). Nulo quando não dá para emitir. */
  contract: {
    required: boolean;
    released: boolean;
    exception: boolean;
    /** Compra dentro de contrato vigente (ADR 0102): o código do contrato escolhido. */
    withinContract?: string | null;
  } | null;
  /** Prévia do pedido quando dá para emitir: dados da proposta aprovada. */
  issue: {
    supplierName: string | null;
    currency: string;
    freightAmount: string;
    total: string;
    paymentTerms: string | null;
    deliveryDays: number | null;
    validUntil: string | null;
    expired: boolean;
    contractingApt: boolean;
    contractingBlockers: string[];
    /** Conferência com o contrato ativo da compra (fluxo C); null sem contrato ativo. */
    contractCheck: PurchaseContractCheck | null;
  } | null;
}

export interface PurchaseAttachment {
  id: string;
  itemId: string | null;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  uploadedBy: { id: string; name: string | null };
  canRemove: boolean;
}

// ----- Cotações (C3b) -----

export type QuotationStatus = 'open' | 'decided' | 'cancelled';
export type QuotationSupplierStatus = 'invited' | 'declined' | 'removed';
export type ProposalStatus = 'current' | 'superseded' | 'withdrawn';
export type SelectionBlocker =
  | 'not_current'
  | 'expired'
  | 'incomplete'
  | 'supplier_not_participating'
  | 'supplier_not_apt'
  | 'exception_required';

export interface QuotationPartner {
  id: string;
  displayNumber: string;
  name: string;
  inactive: boolean;
}

export interface QuotationSupplierView {
  id: string;
  partnerId: string;
  partner: QuotationPartner | null;
  status: QuotationSupplierStatus;
  contactNotes: string | null;
  statusReason: string | null;
  linkStatus: PartnerLinkStatus | null;
  contractingApt: boolean;
  contractingBlockers: ContractingBlocker[];
}

export interface ProposalView {
  id: string;
  quotationSupplierId: string;
  partnerId: string | null;
  version: number;
  status: ProposalStatus;
  currency: string;
  freightAmount: string;
  deliveryDays: number | null;
  paymentTerms: string | null;
  validUntil: string | null;
  receivedOn: string;
  notes: string | null;
  withdrawnReason: string | null;
  createdAt: string;
  createdBy: { id: string | null; name: string | null };
  items: {
    id: string;
    requestItemId: string;
    quantity: string;
    unitPrice: string;
    notes: string | null;
  }[];
  total: string;
  /** Valor que vai para a alçada: os serviços recorrentes contam 12 meses (ADR 0080). */
  approvalAmount: string;
  convertedTotal: string | null;
  rate: { rate: string; inverse: boolean; rateDate: string; source: string | null } | null;
  valid: boolean;
  complete: boolean;
  selected: boolean;
  files: { id: string; fileName: string; sizeBytes: number }[];
}

export interface QuotationActionsView {
  open: boolean;
  inviteSupplier: boolean;
  recordProposal: boolean;
  select: boolean;
  reopen: boolean;
  cancel: boolean;
  submit: boolean;
}

export interface QuotationHistoryItem {
  id: string;
  number: number;
  displayNumber: string;
  cancelledAt: string | null;
  cancelReason: string | null;
}

export interface QuotationView {
  quotation: {
    id: string;
    number: number;
    displayNumber: string;
    status: QuotationStatus;
    responseDue: string | null;
    instructions: string | null;
    selectedProposalId: string | null;
    selectionJustification: string | null;
    minProposalsRequired: number | null;
    validProposalsAtDecision: number | null;
    exceptionJustification: string | null;
    decidedAt: string | null;
    decidedBy: { id: string; name: string | null } | null;
    createdAt: string;
  } | null;
  items?: { id: string; lineNo: number; description: string; quantity: string; unit: string }[];
  suppliers?: QuotationSupplierView[];
  proposals?: ProposalView[];
  comparison?: {
    currency: string | null;
    lowestProposalId: string | null;
    validProposals: number;
    minProposals: number;
  };
  history: QuotationHistoryItem[];
  actions: QuotationActionsView;
}

export interface SupplierOption {
  partnerId: string;
  partner: QuotationPartner | null;
  linkStatus: PartnerLinkStatus | null;
  contractingApt: boolean;
  contractingBlockers: ContractingBlocker[];
}

export interface QuoteRule {
  id: string;
  legalEntityId: string | null;
  legalEntityCode: string | null;
  minAmount: string | null;
  currency: string | null;
  minProposals: number;
  notes: string | null;
  isActive: boolean;
  canEdit: boolean;
}

// ----- Parceiros (ADR 0014) -----

export type PartnerGeneralStatus = 'inactive' | 'blocked' | 'pending' | 'active';
export type PartnerLinkStatus = 'pending' | 'approved' | 'rejected' | 'inactive';
export type RestrictionArea = 'commercial' | 'financial' | 'legal';
export type ContractingBlocker =
  'inactive' | 'not_linked' | 'not_approved' | 'restricted' | 'documents';

export interface EntityLabel {
  code: string;
  name: string;
  country: string;
}

export interface EntityChoice {
  id: string;
  code: string;
  name: string;
}

export interface PartnerSummary {
  id: string;
  number: number;
  displayNumber: string;
  personType: 'individual' | 'company';
  legalName: string;
  tradeName: string | null;
  country: string;
  taxIdType: 'cpf' | 'cnpj' | 'foreign' | 'none';
  taxId: string | null;
  status: PartnerGeneralStatus;
  blockedAreas: RestrictionArea[];
  hasEntityRestrictions: boolean;
  links: {
    legalEntityId: string;
    legalEntity: EntityLabel | null;
    status: PartnerLinkStatus;
    contractingApt: boolean;
  }[];
}

export interface PartnerDuplicate {
  id: string;
  displayNumber: string;
  displayName: string;
  country: string;
  match: 'tax_id' | 'name';
  visible: boolean;
}

export interface PartnerLink {
  id: string;
  legalEntityId: string;
  legalEntity: EntityLabel | null;
  status: PartnerLinkStatus;
  requestedAt: string;
  requestedByName: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionComment: string | null;
  contracting: { apt: boolean; blockers: ContractingBlocker[]; restrictionIds: string[] };
  paymentRestrictionIds: string[];
  actions: {
    decide: boolean;
    decideBlockedBySeparation: boolean;
    inactivate: boolean;
    reactivate: boolean;
    requestAgain: boolean;
  };
}

export interface PartnerContact {
  id: string;
  name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  notes: string | null;
}

export interface PartnerRestriction {
  id: string;
  legalEntityId: string | null;
  legalEntity: EntityLabel | null;
  area: RestrictionArea;
  blocksContracting: boolean;
  blocksPayments: boolean;
  reason: string;
  status: 'active' | 'lifted';
  createdAt: string;
  createdByName: string | null;
  liftedAt: string | null;
  liftedByName: string | null;
  liftJustification: string | null;
  waivers: {
    id: string;
    operationType: string;
    operationId: string;
    justification: string;
    createdAt: string;
    createdByName: string | null;
  }[];
  canLift: boolean;
}

export interface PartnerEvent {
  id: string;
  kind: string;
  occurredAt: string;
  actorName: string | null;
  legalEntity: EntityLabel | null;
  comment: string | null;
  data: Record<string, unknown>;
}

export interface PartnerDetail {
  id: string;
  categoryId: string | null;
  number: number;
  displayNumber: string;
  personType: 'individual' | 'company';
  legalName: string;
  tradeName: string | null;
  country: string;
  taxIdType: 'cpf' | 'cnpj' | 'foreign' | 'none';
  taxId: string | null;
  taxIdExceptionReason: string | null;
  altIdentifier: string | null;
  marketplaceSeller: boolean;
  duplicateCheckNote: string | null;
  addressStreet: string | null;
  addressNumber: string | null;
  addressComplement: string | null;
  addressDistrict: string | null;
  addressCity: string | null;
  addressRegion: string | null;
  addressPostalCode: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  notes: string | null;
  stateRegistration: string | null;
  municipalRegistration: string | null;
  registryStatus: RegistryStatus | null;
  registryCheckedAt: string | null;
  registryStatusNote: string | null;
  inactivatedAt: string | null;
  inactivationReason: string | null;
  inactivatedByName: string | null;
  createdAt: string;
  createdByName: string | null;
  status: PartnerGeneralStatus;
  blockedAreas: RestrictionArea[];
  hasEntityRestrictions: boolean;
  links: PartnerLink[];
  contacts: PartnerContact[];
  restrictions: PartnerRestriction[];
  externalReferences: { id: string; system: string; externalCode: string; notes: string | null }[];
  events: PartnerEvent[];
  actions: {
    edit: boolean;
    inactivate: boolean;
    reactivate: boolean;
    linkableEntities: EntityChoice[];
    restrict: Record<RestrictionArea, { group: boolean; entityIds: string[] }>;
  };
}

// ----- Dados bancários e condições de pagamento dos parceiros (C3a.2, ADR 0016) -----

export type BankAccountKind = 'br_account' | 'pix' | 'iban' | 'swift';
export type BankAccountStatus = 'pending' | 'active' | 'rejected' | 'superseded' | 'inactive';
export type PaymentMethod = 'bank_transfer' | 'pix' | 'boleto' | 'card' | 'other';
export type PaymentBlocker = 'not_linked' | 'restricted' | 'no_confirmed_account';

export interface BankAccountVersion {
  id: string;
  version: number;
  kind: BankAccountKind;
  country: string;
  currency: string;
  holderName: string;
  holderTaxId: string | null;
  bankCode: string | null;
  bankName: string | null;
  branch: string | null;
  accountType: 'checking' | 'savings' | 'payment' | null;
  pixKeyType: 'cpf' | 'cnpj' | 'email' | 'phone' | 'random' | null;
  swiftBic: string | null;
  bankIspb: string | null;
  bankAddress: string | null;
  localCodeType: 'aba' | 'sort_code' | 'bsb' | 'ifsc' | 'ca_routing' | null;
  localCode: string | null;
  intermediarySwift: string | null;
  intermediaryName: string | null;
  intermediaryAccount: string | null;
  /** Só os últimos caracteres, ex.: "•••• 45-6". */
  masked: string;
  status: BankAccountStatus;
  changeReason: string;
  createdByName: string | null;
  createdAt: string;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
  inactivatedAt: string | null;
  inactivationReason: string | null;
  /** Em outras contas pendentes: em quantos outros parceiros a mesma conta aparece. */
  sameAccountOnOtherPartners: number | null;
  actions: {
    decide: boolean;
    decideBlockedBySeparation: boolean;
    /** Quem lançou nota deste fornecedor pelo BackOffice só pode recusar (migração 0262). */
    confirmBlockedByInvoice: boolean;
    inactivate: boolean;
    reveal: boolean;
  };
}

export interface BankAccountGroup {
  accountKey: string;
  current: BankAccountVersion | null;
  pending: BankAccountVersion | null;
  versions: BankAccountVersion[];
  canChange: boolean;
}

export interface PartnerBankAccounts {
  accounts: BankAccountGroup[];
  canAdd: boolean;
  /** Vendedor de marketplace: recebe pela plataforma, sem conta bancária. */
  marketplaceSeller: boolean;
}

export interface PaymentTerms {
  id: string;
  method: PaymentMethod;
  netDays: number;
  installments: number;
  dueDay: number | null;
  notes: string | null;
}

export interface PartnerPaymentEntity {
  legalEntityId: string;
  terms: PaymentTerms | null;
  payment: { apt: boolean; blockers: PaymentBlocker[]; restrictionIds: string[] };
  canEdit: boolean;
}

// ----- Documentos dos parceiros (C3a.3, ADR 0017) -----

export type DocumentSituation =
  'missing' | 'requested' | 'in_review' | 'rejected' | 'valid' | 'expiring' | 'expired';

export interface PartnerCategory {
  id: string;
  code: string;
  name: LocalizedText;
  isActive: boolean;
}

export interface PartnerDocumentType {
  id: string;
  code: string;
  name: LocalizedText;
  legalEntityId: string | null;
  categoryId: string | null;
  requiresExpiry: boolean;
  requiredForApproval: boolean;
  responsibleArea: RestrictionArea;
  sensitivity: 'normal' | 'restricted';
  isActive: boolean;
  canEdit: boolean;
}

export interface PartnerDocuments {
  situations: {
    legalEntityId: string;
    regular: boolean;
    items: {
      documentTypeId: string;
      required: boolean;
      situation: DocumentSituation;
      currentDocumentId: string | null;
      expiresOn: string | null;
    }[];
  }[];
  documents: {
    id: string;
    documentTypeId: string;
    legalEntityId: string | null;
    responsibleArea: RestrictionArea;
    sensitivity: 'normal' | 'restricted';
    fileName: string;
    sizeBytes: number;
    issuedOn: string | null;
    expiresOn: string | null;
    status: 'pending_review' | 'approved' | 'rejected' | 'superseded';
    createdByName: string | null;
    createdAt: string;
    reviewedByName: string | null;
    reviewedAt: string | null;
    reviewComment: string | null;
    actions: { review: boolean; reviewBlockedBySeparation: boolean; download: boolean };
  }[];
  requests: {
    id: string;
    documentTypeId: string;
    legalEntityId: string | null;
    dueDate: string | null;
    note: string | null;
    status: 'open' | 'fulfilled' | 'cancelled';
    createdByName: string | null;
    createdAt: string;
    canCancel: boolean;
  }[];
  actions: { submit: boolean };
}

export interface PendingDocument {
  partnerId: string;
  displayNumber: string;
  legalName: string;
  legalEntityId: string;
  legalEntity: { code: string; name: string } | null;
  documentTypeId: string;
  required: boolean;
  situation: DocumentSituation;
  expiresOn: string | null;
}

// ----- Central de Conhecimento (ADR 0018) -----

export type KnowledgeState =
  'in_review' | 'approved' | 'rejected' | 'published' | 'superseded' | 'revoked';

export interface KnowledgeSummary {
  id: string;
  /** Documento a que a versão pertence (o que o administrador master exclui). */
  documentId: string;
  code: string;
  version: string;
  title: string;
  docType: 'mestre' | 'manual' | 'pop' | 'ito' | 'faq' | 'novidade';
  department: string;
  modules: string[];
  process: string | null;
  contentOwner: string;
  approverRole: string;
  reviewerRoles: string[];
  confidentiality: 'interno' | 'restrito';
  repoStatus: 'rascunho' | 'obsoleto';
  revisedOn: string | null;
  keywords: string[];
  related: string[];
  sourcePath: string;
  authorEmail: string | null;
  state: KnowledgeState;
  importedAt: string;
  sha256: string;
  installedRelease: string | null;
}

export interface KnowledgeDocumentView {
  code: string;
  current: (KnowledgeSummary & { body: string }) | null;
  preview: (KnowledgeSummary & { body: string }) | null;
  versions: KnowledgeSummary[];
  related: { code: string; title: string; docType: string }[];
}

export interface KnowledgeVersionView extends KnowledgeSummary {
  body: string;
  decisions: {
    id: string;
    kind: 'review_ok' | 'review_changes' | 'approved' | 'rejected' | 'published' | 'revoked';
    roleCode: string | null;
    comment: string | null;
    sha256: string;
    actorName: string;
    createdAt: string;
  }[];
  pendingReviewers: string[];
  actions: {
    reviewAs: string[];
    approve: boolean;
    reject: boolean;
    publish: boolean;
    revoke: boolean;
    approveBlockedAsAuthor: boolean;
  };
}

export interface KnowledgeImportIssue {
  id: string;
  sourcePath: string;
  code: string | null;
  version: string | null;
  problem: string;
  createdAt: string;
}

// ----- Financeiro (ADR 0068) -----

export interface NetsuiteReferenceView {
  code: string | null;
  createdAt: string;
  createdByName: string | null;
  removedAt: string | null;
}

export interface NetsuiteReferences {
  kind: 'internal_id' | 'record_number';
  current: NetsuiteReferenceView | null;
  history: NetsuiteReferenceView[];
  canEdit: boolean;
}

export type TreasuryAccountKind = 'br_account' | 'iban' | 'us_account' | 'foreign_account';
export type TreasuryAccountStatus = 'pending' | 'active' | 'rejected' | 'superseded' | 'inactive';

export interface TreasuryAccountVersion {
  id: string;
  version: number;
  nickname: string;
  kind: TreasuryAccountKind;
  country: string;
  currency: string;
  bankName: string;
  bankCode: string | null;
  ispb: string | null;
  branch: string | null;
  accountType: string | null;
  routingNumber: string | null;
  swiftBic: string | null;
  pixKey: string | null;
  pixKeyType: string | null;
  bankAddress: string | null;
  localCodeType: string | null;
  localCode: string | null;
  intermediarySwift: string | null;
  intermediaryName: string | null;
  intermediaryAccount: string | null;
  purpose: string;
  validFrom: string;
  validUntil: string | null;
  integrationChannel: string;
  integrationNotes: string | null;
  responsibleUserId: string | null;
  responsibleName: string | null;
  masked: string;
  status: TreasuryAccountStatus;
  changeReason: string;
  createdByName: string | null;
  createdAt: string;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
  inactivatedAt: string | null;
  inactivationReason: string | null;
  actions: {
    decide: boolean;
    decideBlockedBySeparation: boolean;
    inactivate: boolean;
    reveal: boolean;
  };
}

export interface TreasuryAccountGroup {
  accountKey: string;
  legalEntityId: string;
  legalEntityCode: string | null;
  legalEntityName: string | null;
  situation:
    'usable' | 'out_of_validity' | 'awaiting_confirmation' | 'inactive' | 'rejected' | 'superseded';
  current: TreasuryAccountVersion | null;
  pending: TreasuryAccountVersion | null;
  latest: TreasuryAccountVersion;
  versions: TreasuryAccountVersion[];
  canChange: boolean;
}

export interface TreasuryAccounts {
  accounts: TreasuryAccountGroup[];
  canAdd: boolean;
  addScope: { group: boolean; entityIds: string[] };
}

// ----- Caixa da Tesouraria (ADR 0084, Entrega 2a) -----

export interface CashAccount {
  accountKey: string;
  legalEntityId: string;
  legalEntityCode: string | null;
  legalEntityName: string | null;
  nickname: string;
  bankName: string;
  currency: string;
  masked: string;
  accountStatus: string;
  openingDate: string | null;
  lastReported: { date: string; amount: string } | null;
  lastCoveredDay: string | null;
  canManage: boolean;
  /** Banco ou plataforma (D-03). */
  kind: 'bank' | 'platform';
  payoutAccountKey: string | null;
  /** Bloqueios e reservas que valem hoje (D-02). */
  held: string;
  /** Confirmado − bloqueios; nulo sem saldo confirmado. */
  available: string | null;
}

export interface BalanceHold {
  id: string;
  kind: 'judicial' | 'guarantee' | 'reserve' | 'other';
  amount: string;
  startDate: string;
  endDate: string | null;
  reason: string;
  status: 'active' | 'released';
  releaseReason: string | null;
  releasedAt: string | null;
}

export interface PlatformAccount {
  id: string;
  legalEntityId: string;
  legalEntityCode: string;
  platform: string;
  nickname: string;
  currency: string;
  payoutAccountKey: string | null;
  status: 'active' | 'inactive';
  notes: string | null;
  inactivatedReason: string | null;
  canManage: boolean;
}

/** Recebimento previsto ou recebido (ADR 0084, Entrega 5). */
export interface CashReceipt {
  id: string;
  accountKey: string;
  accountName: string | null;
  accountKind: 'bank' | 'platform' | null;
  currency: string;
  status: 'expected' | 'received' | 'cancelled' | 'reversed';
  expectedDate: string;
  effectiveDate: string | null;
  grossAmount: string;
  feeAmount: string;
  withholdingAmount: string;
  netAmount: string;
  origin: string;
  partnerId: string | null;
  payerName: string | null;
  document: string | null;
  description: string;
  expenseTypeId: string | null;
  classificationId: string | null;
  costCenterId: string | null;
  notes: string | null;
  statusReason: string | null;
  createdAt: string;
}

/** Recebimento atrasado, na lista de todas as empresas que o cartão da Visão geral abre. */
export interface LateCashReceipt extends CashReceipt {
  legalEntityCode: string | null;
  canManage: boolean;
}

export interface ReceiptFormOptions {
  expenseTypes: { id: string; code: string | null; name: string }[];
  classifications: {
    id: string;
    code: string | null;
    name: string;
    expenseTypeIds: string[] | null;
  }[];
  costCenters: { id: string; code: string; name: string }[];
  partners: { id: string; name: string }[];
}

export type CashDayStatus = 'before_opening' | 'pending' | 'reconciled' | 'divergent';

export interface CashDay {
  date: string;
  covered: boolean;
  opening: string | null;
  credits: string;
  debits: string;
  calculated: string | null;
  reported: string | null;
  reportedId: string | null;
  difference: string | null;
  status: CashDayStatus;
  adjusted: boolean;
  settlementsOnCoveredDay: string;
}

export interface CashPositions {
  account: { accountKey: string; currency: string; nickname: string };
  openingDate: string | null;
  from: string;
  to: string;
  days: CashDay[];
}

export interface CashMovement {
  id: string;
  origin: 'statement' | 'adjustment';
  statementImportId: string | null;
  valueDate: string;
  direction: 'in' | 'out';
  amount: string;
  currency: string;
  description: string;
  bankReference: string | null;
  kind: string;
  reversesId: string | null;
  note: string | null;
  reversed: boolean;
  canReverse: boolean;
}

export interface CashStatement {
  id: string;
  format: string;
  periodStart: string;
  periodEnd: string;
  fileName: string | null;
  linesRead: number;
  linesRecorded: number;
  linesRepeated: number;
  status: 'recorded' | 'discarded';
  discardReason: string | null;
  createdByName: string | null;
  createdAt: string;
  discardedByName: string | null;
  discardedAt: string | null;
  canDiscard: boolean;
}

export interface CashAdjustment {
  id: string;
  accountKey: string;
  currency: string;
  adjustmentDate: string;
  direction: 'in' | 'out';
  amount: string;
  reason: string;
  status: 'proposed' | 'confirmed' | 'rejected';
  createdByName: string | null;
  createdAt: string;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
  actions: { decide: boolean; decideBlockedBySeparation: boolean };
}

export interface CashTransfer {
  id: string;
  fromEntityId: string;
  fromAccountKey: string;
  fromCurrency: string;
  fromAmount: string;
  outDate: string;
  toEntityId: string;
  toAccountKey: string;
  toCurrency: string;
  toAmount: string;
  inDate: string;
  exchangeRate: string | null;
  feeAmount: string;
  isIntercompany: boolean;
  description: string;
  status: 'recorded' | 'cancelled';
  cancelReason: string | null;
  canCancel: boolean;
}

// ----- Projeção do caixa (ADR 0084, Entrega 3) -----

export interface CashSettingsEntity {
  legalEntityId: string;
  code: string;
  name: string;
  functionalCurrency: string;
  settings: {
    minimumBalance: string;
    minimumCurrency: string;
    consolidationCurrency: string;
  } | null;
  canManage: boolean;
}

export interface ProjectionItem {
  sourceType:
    | 'payables.installment'
    | 'treasury.forecast_occurrence'
    | 'treasury.forecast_payroll'
    | 'treasury.receipt'
    | 'treasury.budget_estimate'
    | 'treasury.scenario';
  sourceId: string;
  accountKey: string | null;
  currency: string;
  date: string;
  projectedDate: string;
  amount: string;
  layer: 'commitment' | 'forecast' | 'receivable' | 'budget' | 'scenario';
  basis: 'scheduled' | 'due' | 'overdue' | 'forecast' | 'expected' | 'estimate';
  label: string;
}

/** Vínculo de uma ocorrência prevista com o título real (ADR 0084, Entrega 4). */
export interface CashForecastLink {
  id: string;
  targetType: string;
  targetId: string;
  label: string | null;
  amount: string;
  createdAt: string;
  undoneAt: string | null;
  undoReason: string | null;
}

export interface CashForecastOccurrence {
  id: string;
  sequence: number;
  dueDate: string;
  amount: string;
  overridden: boolean;
  status: 'open' | 'closed' | 'cancelled';
  closeReason: string | null;
  linked: string;
  residual: string;
  links: CashForecastLink[];
}

export interface CashForecastAttachment {
  id: string;
  fileName: string;
  title: string | null;
  sizeBytes: number;
  createdAt: string;
}

/** Opções do formulário de previsão (pessoal só para quem vê pessoal, D-04). */
export interface ForecastFormOptions {
  seesPayroll: boolean;
  expenseTypes: { id: string; code: string | null; name: string; isPayroll: boolean }[];
  classifications: {
    id: string;
    code: string | null;
    name: string;
    isPayroll: boolean;
    expenseTypeIds: string[] | null;
  }[];
  costCenters: { id: string; code: string; name: string }[];
  partners: { id: string; name: string }[];
}

/** Lançamento previsto e suas ocorrências (GET /finance/treasury/forecasts). */
export interface CashForecast {
  id: string;
  legalEntityId: string;
  branchId: string | null;
  direction: 'in' | 'out' | 'transfer';
  description: string;
  amount: string;
  currency: string;
  competence: string;
  accountKey: string | null;
  toAccountKey: string | null;
  expenseTypeId: string | null;
  classificationId: string | null;
  costCenterId: string | null;
  partnerId: string | null;
  nature: 'operating' | 'investing' | 'financing';
  certainty: 'confirmed' | 'probable' | 'estimated' | 'scenario';
  frequency: ForecastFrequency;
  customUnit: 'day' | 'week' | 'month' | null;
  customEvery: number | null;
  isPayroll: boolean;
  startDate: string;
  endDate: string | null;
  maxOccurrences: number | null;
  previousId: string | null;
  notes: string | null;
  status: 'active' | 'cancelled';
  cancelReason: string | null;
  createdAt: string;
  occurrences: CashForecastOccurrence[];
  attachments: CashForecastAttachment[];
}

export interface ProjectionDayRow {
  accountKey: string | null;
  currency: string;
  date: string;
  opening: string | null;
  inflows: string;
  outflows: string;
  closing: string | null;
  flags: ('no_balance' | 'unassigned')[];
}

export interface ProjectionTotal {
  currency: string;
  date: string;
  closing: string;
  inflows: string;
  outflows: string;
  overdue: string;
  flags: ('negative' | 'below_minimum' | 'partial')[];
}

export interface CashProjection {
  legalEntityId: string;
  asOf: string;
  horizonEnd: string;
  minimum: { amount: string; currency: string };
  calendarAssigned: boolean;
  accounts: {
    accountKey: string;
    nickname: string;
    currency: string;
    openingDate: string | null;
  }[];
  items: ProjectionItem[];
  days: ProjectionDayRow[];
  totals: ProjectionTotal[];
  summaries: {
    currency: string;
    lowest: { date: string; closing: string };
    firstNegative: string | null;
    firstBelowMinimum: string | null;
    partial: boolean;
  }[];
  origins: Record<string, string>;
  /** Visão consolidada numa moeda (D-08): só na exibição, pela taxa do dia de cada moeda. */
  consolidation: {
    currency: string;
    rates: {
      currency: string;
      rate: string;
      inverse: boolean;
      rateDate: string;
      source: string | null;
      rateType: string | null;
    }[];
    missing: string[];
    totals: ProjectionTotal[];
    summary: CashProjection['summaries'][number] | null;
  };
}

export interface CashSnapshot {
  id: string;
  kind: 'daily' | 'on_demand';
  asOf: string;
  horizonEnd: string;
  engineVersion: string;
  createdAt: string;
  createdByName: string | null;
}

// ----- Conciliação bancária (ADR 0084, Entrega 9) -----

export type ReconBookType =
  'payables.movement' | 'treasury.transfer.out' | 'treasury.transfer.in' | 'treasury.receipt';

export type ReconSuggestion =
  | { bankId: string; status: 'suggested'; book: { type: ReconBookType; id: string } }
  | { bankId: string; status: 'ambiguous'; candidates: { type: ReconBookType; id: string }[] }
  | { bankId: string; status: 'none' };

export interface ReconBankLine {
  id: string;
  date: string;
  amount: string;
  reference: string | null;
  description: string;
  kind: string;
  suggestion: ReconSuggestion | null;
  /** Pendente; divergente ou em análise quando uma divergência aberta aponta a linha. */
  situation: 'pending' | 'divergent' | 'in_review';
}

export interface ReconBookItem {
  type: ReconBookType;
  id: string;
  date: string;
  amount: string;
  reference: string | null;
  label: string;
}

export interface Reconciliation {
  id: string;
  bankTotal: string;
  bookTotal: string;
  difference: string;
  differenceKind: string | null;
  reason: string | null;
  method: 'suggested' | 'manual';
  /** Conciliado; com diferença justificada, ajustado. */
  situation: 'reconciled' | 'adjusted';
  createdAt: string;
  createdByName: string | null;
  lines: {
    side: 'bank' | 'book';
    itemType: string;
    itemId: string;
    amount: string;
    valueDate: string;
  }[];
  undone: { reason: string; at: string; byName: string | null } | null;
  canUndo: boolean;
}

/** Divergência da conferência da conta (ADR 0084, §15). */
export interface CashDivergence {
  id: string;
  accountKey: string;
  currency: string;
  kind: string;
  refDate: string | null;
  amount: string | null;
  details: Record<string, unknown>;
  status: 'pending' | 'in_review' | 'resolved' | 'dismissed';
  note: string | null;
  resolution: string | null;
  resolvedAutomatically: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
  reviewStartedAt: string | null;
  reviewStartedByName: string | null;
  closedAt: string | null;
  closedByName: string | null;
}

export interface ReconOverview {
  account: { accountKey: string; currency: string; nickname: string };
  from: string;
  to: string;
  canReconcile: boolean;
  bankLines: ReconBankLine[];
  bookItems: ReconBookItem[];
  reconciliations: Reconciliation[];
  discrepancies: { bankWithoutBook: number; bookWithoutBank: number };
}

// ----- Contas a Pagar (ADR 0081) -----

export type PayableStatus =
  'draft' | 'in_approval' | 'approved' | 'partially_paid' | 'paid' | 'cancelled' | 'rejected';

export interface PayableListItem {
  id: string;
  code: string;
  legalEntityId: string;
  legalEntityCode: string | null;
  partnerName: string | null;
  description: string;
  documentNumber: string | null;
  currency: string;
  grossAmount: string;
  netAmount: string;
  openAmount: string;
  nextDue: string | null;
  platformPaid: boolean;
  status: PayableStatus;
}

/** Linha da lista por parcela que os cartões da Visão geral abrem. */
export interface PayableQueueInstallment {
  id: string;
  payableId: string;
  code: string;
  number: number;
  legalEntityId: string;
  legalEntityCode: string | null;
  partnerName: string | null;
  description: string;
  currency: string;
  amount: string;
  openAmount: string;
  dueDate: string;
  scheduledFor: string | null;
}

export interface PayableMovementView {
  id: string;
  kind: 'payment' | 'reversal' | 'cancellation';
  /** O que abate do saldo da parcela (principal). */
  amount: string | null;
  interestAmount: string;
  fineAmount: string;
  discountAmount: string;
  /** Valor que saiu do caixa (principal + juros + multa - desconto); nulo no estorno. */
  cashAmount: string | null;
  occurredOn: string;
  reference: string | null;
  note: string | null;
  acknowledgedWarnings: string[];
  createdByName: string | null;
  createdAt: string;
  reversed: boolean;
  canReverse: boolean;
}

export interface PayableInstallmentView {
  id: string;
  number: number;
  dueDate: string;
  amount: string;
  paidAmount: string;
  openAmount: string;
  status: 'open' | 'scheduled' | 'partially_paid' | 'paid' | 'cancelled';
  scheduledFor: string | null;
  treasuryAccountKey: string | null;
  movements: PayableMovementView[];
  canSchedule: boolean;
  canPay: boolean;
  payBlockedBySeparation: boolean;
}

/** Fornecedor para escolher num formulário: nome, código e documento (CNPJ ou estrangeiro). */
export interface PartnerChoice {
  id: string;
  name: string;
  number: number;
  taxIdType: 'cnpj' | 'cpf' | 'foreign' | 'none';
  taxId: string | null;
}

/** Ficha mínima da resposta do gestor ao título provisório no Master (ADR 0094). */
export interface ProvisionalReviewDetail {
  id: string;
  payableCode: string;
  payableStatus: PayableStatus;
  status: 'pending' | 'confirmed' | 'cadastre_error';
  answerNote: string | null;
  answeredAt: string | null;
  reason: string;
  grossAmount: string;
  currency: string;
  competence: string;
  contractCode: string;
  contractTitle: string;
  partnerName: string;
  firstDueDate: string | null;
  firstDeadline: string | null;
  finalDeadline: string | null;
  supplierNoticeAt: string | null;
  canAnswer: boolean;
}

export interface PayableDetail {
  id: string;
  code: string;
  legalEntityId: string;
  legalEntityCode: string | null;
  originKind: 'purchase_order' | 'manual';
  partnerId: string | null;
  partnerName: string | null;
  documentType: string | null;
  documentNumber: string | null;
  documentSeries: string | null;
  documentDate: string | null;
  description: string;
  competence: string;
  currency: string;
  grossAmount: string;
  withholdingAmount: string;
  netAmount: string;
  paymentMethod: string;
  terms: {
    source: string | null;
    netDays: number | null;
    installments: number | null;
    dueDay: number | null;
    originText: string | null;
  };
  status: PayableStatus;
  cancelReason: string | null;
  createdByName: string | null;
  createdAt: string;
  withholdings: {
    taxCode: string;
    baseAmount: string | null;
    rate: string | null;
    amount: string;
    note: string | null;
  }[];
  /** O que falta no aceite de Compras para pagar (`null` = nada). */
  acceptanceIssue: 'order_item_not_accepted' | 'order_part_not_accepted' | null;
  /** Compra de marketplace cobrada pela plataforma antes do aceite: não libera o pagamento. */
  platformOccurrences: {
    id: string;
    paidOn: string;
    amount: string;
    currency: string;
    reference: string;
    note: string | null;
    createdAt: string;
    createdByName: string | null;
  }[];
  canSignalPlatform: boolean;
  /** Resposta do CAC e de onde veio; vazia em título anterior à regra (ADR 0094). */
  affectsCac: boolean | null;
  affectsCacSource: 'purchase' | 'payable' | null;
  /** Rateio do título (no de pedido, as parcelas do item); sem nome o que a pessoa não vê. */
  allocationProvisional: boolean;
  allocationProvisionalReason: string | null;
  provisionalReview: {
    id: string;
    status: ProvisionalReviewDetail['status'];
    answerNote: string | null;
    answeredAt: string | null;
    firstDeadline: string | null;
    finalDeadline: string | null;
    supplierNoticeAt: string | null;
    managerName: string | null;
  } | null;
  allocation: {
    costCenterId: string;
    costCenterLabel: string | null;
    legalEntityCode: string | null;
    percent: string;
    amount?: string;
    isPayer?: boolean;
  }[];
  installments: PayableInstallmentView[];
  actions: { submit: boolean; withdraw: boolean; cancel: boolean };
}

export interface PayableFormOptions {
  expenseTypes: { id: string; code: string | null; name: string }[];
  classifications: {
    id: string;
    code: string | null;
    name: string;
    expenseTypeIds: string[] | null;
  }[];
  costCenters: { id: string; code: string; name: string; isMarketing: boolean }[];
  /** Sem o CPF: só CNPJ e documento estrangeiro. */
  partners: PartnerChoice[];
  treasuryAccounts: { accountKey: string; nickname: string; currency: string; masked: string }[];
}

export type InvoiceStatus =
  'received' | 'in_review' | 'pending' | 'rejected' | 'converted' | 'divergent' | 'awaiting_order';
export type InvoiceDocumentKind = 'nfe' | 'nfse' | 'invoice';

/** Nota de fornecedor recebida (ADR 0081, C9): portal ou lançada pela Loot. */
export interface InvoiceListItem {
  id: string;
  code: string;
  legalEntityId: string;
  legalEntityCode: string | null;
  partnerId: string;
  partnerName: string | null;
  channel: 'backoffice' | 'portal';
  documentKind: InvoiceDocumentKind;
  documentNumber: string;
  documentSeries: string | null;
  issueDate: string;
  currency: string;
  grossAmount: string;
  reference: string | null;
  status: InvoiceStatus;
  createdAt: string;
}

/** O que a nota declara no XML, para preencher o título (quem lança confere). */
export interface InvoiceDeclared {
  competence: string | null;
  netAmount: string | null;
  withholdings: {
    taxCode: 'IRRF' | 'ISS' | 'INSS' | 'PIS' | 'COFINS' | 'CSLL' | 'PCC';
    baseAmount: string | null;
    rate: string | null;
    amount: string;
  }[];
  installments: { dueDate: string; amount: string }[];
  paymentMeans: 'cash' | 'credit_card' | 'debit_card' | 'boleto' | 'pix' | 'other' | null;
}

export interface InvoiceDetail extends InvoiceListItem {
  fromXml: InvoiceDeclared | null;
  arrivalMeans: 'email' | 'paper' | 'other' | null;
  arrivalNote: string | null;
  enteredBy: string | null;
  accessKey: string | null;
  orderId: string | null;
  contractId: string | null;
  supplierNote: string | null;
  statusReason: string | null;
  attachments: {
    id: string;
    kind: 'xml' | 'pdf' | 'evidence';
    fileName: string;
    sizeBytes: number;
  }[];
  events: { status: InvoiceStatus; reason: string | null; at: string; by: string | null }[];
  payables: { id: string; code: string; status: PayableStatus; grossAmount: string }[];
  /** Divergência de valor (D1): interna, nunca vai ao portal. */
  divergenceOpen: boolean;
  divergences: {
    kind: 'detected' | 'invoice_error' | 'order_error' | 'rejected' | 'resolved';
    order: string | null;
    invoiceAmount: string | null;
    openAmount: string | null;
    tolerancePercent: string | null;
    reason: string | null;
    at: string;
    by: string | null;
  }[];
  canAnalyzeDivergence: boolean;
  divergenceOrders: { id: string; label: string; openAmount: string }[];
}

/** Parte do item que vira título: o item inteiro (sem parte) ou um aceite, etapa ou período. */
export type OrderItemBilling =
  | { basis: 'acceptance'; acceptanceLineId: string }
  | { basis: 'milestone'; milestoneNo: number }
  | { basis: 'period'; periodStart: string };

/** Itens do pedido da nota, para gerar os títulos (ADR 0081, C9, F1). */
export interface InvoiceOrderItems {
  invoiceRemaining: string;
  items: {
    orderItemId: string;
    description: string;
    lineTotal: string;
    open: string;
    fullAvailable: boolean;
    partialAvailable: boolean;
    expenseTypeId: string | null;
    classificationId: string | null;
    costCenterId: string | null;
    parts: { billing: OrderItemBilling; quantity: string | null; acceptedAt: string | null }[];
  }[];
}

/** O que a tela da nota entrega ao formulário do título quando a nota é de pedido. */
export interface PayableOrderLine {
  orderItemId: string;
  billing: OrderItemBilling | null;
  amount: string;
  description: string;
  expenseTypeId: string | null;
  classificationId: string | null;
  costCenterId: string | null;
}

export interface InvoiceReference {
  kind: 'purchase_order' | 'contract';
  id: string;
  label: string;
  currency: string | null;
  openAmount: string | null;
}

export interface PayableAging {
  asOf: string;
  rows: {
    legalEntityId: string;
    legalEntityCode: string | null;
    currency: string;
    buckets: Record<'not_due' | 'd1_30' | 'd31_60' | 'd61_90' | 'over_90', string>;
    total: string;
  }[];
}
